import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, MapPin, Award, FileText, Lock, Building2, Briefcase, Pencil, X, Check,
  Plus, Trash2, Upload, CalendarDays, Book, BrainCircuit, MessageCircleQuestion, Eye, ChevronRight,
  UserPlus, UserCheck, Activity, Sparkles, AtSign, Phone, Globe, ExternalLink,
  Share2, Star, EyeOff, BadgeCheck, Handshake, TrendingUp
} from 'lucide-react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLang } from '../hooks/useLang';
import Navbar from '../components/Navbar';
import PublicNavbar from '../components/common/Navbar';
import { detectPlatform, isValidUrl, openExternalLink } from '../utils/socialLinks';
import { uploadCv, setDefaultCv } from '../services/cvService';
import ContributionHeatmap from '../components/profile/ContributionHeatmap';
import ImageUrlModal from '../components/profile/ImageUrlModal';
import formatCount from '../utils/formatCount';

const CATEGORY_ICON = { Book, Note: FileText, Exercise: MessageCircleQuestion, Quiz: BrainCircuit };

const StatusLine = ({ status }) => {
  if (!status) return null;
  return (
    <p className={`text-xs mt-2 ${status.type === 'error' ? 'text-destructive' : 'text-accent'}`}>
      {status.message}
    </p>
  );
};

const SectionHeading = ({ children, action }) => (
  <div className="flex items-center justify-between gap-3 mb-4">
    <h2 className="text-sm font-bold text-foreground">{children}</h2>
    {action}
  </div>
);

const EmptySection = ({ children, action }) => (
  <div className="py-10 px-6 text-center border border-dashed border-border rounded-xl">
    <p className="text-xs text-muted-foreground">{children}</p>
    {action && <div className="mt-4 flex justify-center">{action}</div>}
  </div>
);

// Sends the owner to Settings → Personal information, where education,
// projects, certificates and interests are actually edited.
const ManageButton = ({ onClick, label = 'Manage', icon: Icon = Pencil }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors border-none cursor-pointer flex-shrink-0"
  >
    <Icon className="w-3 h-3" /> {label}
  </button>
);

// Icon-only rail so the profile card can stay dead-centre on the page.
// Hovering expands it leftwards (its right edge is pinned to the card), so
// the labels appear without the card shifting a single pixel.
const SectionRail = ({ sections, activeSection, onSelect, vertical }) => (
  <nav
    className={`group bg-card border border-border rounded-2xl shadow-xl p-2 flex gap-1 transition-[width] duration-300 ease-out ${
      vertical ? 'flex-col w-[60px] hover:w-[200px]' : 'flex-row overflow-x-auto'
    }`}
  >
    {sections.map((section) => {
      const isActive = activeSection === section.id;
      return (
        <button
          key={section.id}
          onClick={() => onSelect(section.id)}
          title={section.label}
          aria-label={section.label}
          className={`flex items-center gap-3 h-11 px-3 rounded-xl border-none cursor-pointer transition-colors flex-shrink-0 ${
            isActive ? 'bg-primary/10 text-primary' : 'bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted'
          }`}
        >
          <section.icon className="w-[18px] h-[18px] flex-shrink-0" />
          <span
            className={`flex items-center gap-2 overflow-hidden transition-all duration-300 ease-out ${
              vertical ? 'max-w-0 opacity-0 group-hover:max-w-[130px] group-hover:opacity-100' : 'max-w-[160px]'
            }`}
          >
            <span className="text-xs font-bold whitespace-nowrap">{section.label}</span>
            {section.count > 0 && (
              <span className={`text-[10px] font-bold px-1.5 rounded-full ${isActive ? 'bg-primary/15' : 'bg-muted'}`}>
                {section.count}
              </span>
            )}
          </span>
        </button>
      );
    })}
  </nav>
);

const Profile = ({ username }) => {
  const { token, dispatch } = useAuthContext();
  const { language } = useLang();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [isSelf, setIsSelf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstname: '', lastname: '', username: '', headline: '', bio: '', socialLinks: [],
    showContact: false, openToTutoring: false,
  });
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [imageUrlModal, setImageUrlModal] = useState({ open: false, type: null });
  const [imageUrlError, setImageUrlError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [contactRevealed, setContactRevealed] = useState(false);
  // Lets the owner see exactly what a stranger sees before sharing the link.
  const [previewAsVisitor, setPreviewAsVisitor] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [pinBusy, setPinBusy] = useState(false);

  // Owner-only chrome (edit buttons, empty-state prompts, the completeness
  // checklist) hides while previewing, so the preview is honest.
  const ownerView = isSelf && !previewAsVisitor;

  const profileUrl = profile ? `${window.location.origin}/${profile.username}` : '';

  const handleShare = async () => {
    if (!profileUrl) return;
    try {
      await navigator.clipboard.writeText(profileUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard can be blocked (insecure origin, permissions) — fall back
      // to the share sheet where it exists, otherwise just do nothing loud.
      if (navigator.share) navigator.share({ url: profileUrl, title: `${profile.firstname} on YeniDers` }).catch(() => {});
    }
  };

  // Pinning is capped at three so "Featured" stays a curated shortlist
  // rather than a second copy of the uploads list.
  const handleToggleFeatured = async (materialId) => {
    if (!token || pinBusy) return;
    const current = Array.isArray(profile.featured_material_ids) ? profile.featured_material_ids : [];
    const next = current.includes(materialId)
      ? current.filter((id) => id !== materialId)
      : [...current, materialId].slice(-3);

    setPinBusy(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/auth/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ featured_material_ids: next }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not update featured work.');
      dispatch({ type: 'UPDATE_USER', payload: data.user });
      const pool = [...(profile.featuredMaterials || []), ...(profile.recentMaterials || [])];
      setProfile((prev) => ({
        ...prev,
        featured_material_ids: next,
        featuredMaterials: next
          .map((id) => pool.find((m) => m.id === id))
          .filter(Boolean),
      }));
    } catch (err) {
      setEditStatus({ type: 'error', message: err.message });
    } finally {
      setPinBusy(false);
    }
  };

  const handleToggleFollow = async () => {
    if (!token || !profile) return;
    setFollowBusy(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/users/${profile.username}/follow`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setProfile(prev => ({ ...prev, isFollowing: data.isFollowing, followerCount: data.followerCount }));
      }
    } catch (err) {
      console.error('Failed to toggle follow:', err);
    } finally {
      setFollowBusy(false);
    }
  };

  const openImageUrlModal = (type) => {
    setImageUrlError(null);
    setImageUrlModal({ open: true, type });
  };

  const handleSaveImageUrl = async (url) => {
    const { type } = imageUrlModal;
    setUploadingImage(true);
    setImageUrlError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/profile-image/${type}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to set image.');
      setProfile(prev => ({ ...prev, [`${type}_url`]: data.url }));
      if (isSelf && type === 'avatar') {
        dispatch({ type: 'UPDATE_USER', payload: { [`${type}_url`]: data.url } });
      }
      setImageUrlModal({ open: false, type: null });
    } catch (err) {
      setImageUrlError(err.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploadingCv(true);
    try {
      const result = await uploadCv(file, token);
      await setDefaultCv(result.cv.id, token);
      setProfile(prev => ({ ...prev, cv: { name: result.cv.name, url: result.cv.url } }));
    } catch (err) {
      setEditStatus({ type: 'error', message: err.message });
    } finally {
      setUploadingCv(false);
    }
  };

  const MAX_BIO_WORDS = 200;
  const countWords = (text) => (text.trim() ? text.trim().split(/\s+/).length : 0);
  const handleBioChange = (value) => {
    const words = value.trim() ? value.trim().split(/\s+/) : [];
    if (words.length > MAX_BIO_WORDS) {
      setEditForm(prev => ({ ...prev, bio: words.slice(0, MAX_BIO_WORDS).join(' ') }));
      return;
    }
    setEditForm(prev => ({ ...prev, bio: value }));
  };

  const handleLinkChange = (index, value) => {
    setEditForm(prev => {
      const next = [...prev.socialLinks];
      next[index] = value;
      return { ...prev, socialLinks: next };
    });
  };
  const addLinkField = () => setEditForm(prev => ({ ...prev, socialLinks: [...prev.socialLinks, ''] }));
  const removeLinkField = (index) => setEditForm(prev => ({ ...prev, socialLinks: prev.socialLinks.filter((_, i) => i !== index) }));

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/users/${username}`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to load profile.');
        }
        setProfile(data.profile);
        setIsSelf(data.isSelf);
        setContactRevealed(false);
        setEditForm({
          firstname: data.profile.firstname || '',
          lastname: data.profile.lastname || '',
          username: data.profile.username || '',
          bio: data.profile.bio || '',
          headline: data.profile.headline || '',
          socialLinks: data.profile.social_links?.length ? data.profile.social_links : [],
          showContact: Boolean(data.profile.show_contact_info),
          openToTutoring: Boolean(data.profile.available_for_tutoring),
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (username) fetchProfile();
  }, [username, token]);

  // A shared profile link opens on a tab that says who it belongs to,
  // instead of the generic app title.
  useEffect(() => {
    if (!profile) return undefined;
    const previous = document.title;
    const fullName = `${profile.firstname || ''} ${profile.lastname || ''}`.trim() || profile.username;
    document.title = profile.headline ? `${fullName} — ${profile.headline}` : `${fullName} (@${profile.username}) · YeniDers`;
    return () => { document.title = previous; };
  }, [profile]);

  const initials = profile
    ? `${profile.firstname?.[0] || ''}${profile.lastname?.[0] || ''}`.toUpperCase() || profile.username?.substring(0, 2).toUpperCase()
    : '';

  const memberSince = profile?.createdAt
    ? new Date(profile.createdAt).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    : null;

  const educationEntries = useMemo(() => {
    if (!profile) return [];
    const history = Array.isArray(profile.education_history) ? profile.education_history : [];
    if (history.length) return history;
    // Falls back to the flat student fields for accounts that never filled
    // in the structured history during onboarding.
    if (profile.university || profile.major || profile.education_level) {
      return [{
        level: profile.education_level,
        institution: profile.university || profile.institution,
        major: profile.major,
        year: profile.year_of_study,
      }];
    }
    return [];
  }, [profile]);

  const teachingEntries = useMemo(
    () => (Array.isArray(profile?.subjects_taught) ? profile.subjects_taught : []),
    [profile]
  );

  const primaryEducation = educationEntries[0] || null;

  // What a profile needs before it is worth handing to somebody. Shown only
  // to the owner, and only until they have finished it.
  const completeness = useMemo(() => {
    if (!profile) return { items: [], done: 0, percent: 100 };
    const items = [
      { id: 'avatar', label: 'Add a profile photo', done: Boolean(profile.avatar_url), action: 'avatar' },
      { id: 'headline', label: 'Write a one-line headline', done: Boolean(profile.headline), action: 'edit' },
      { id: 'bio', label: 'Write a short about section', done: Boolean(profile.bio), action: 'edit' },
      { id: 'education', label: 'Add your education', done: educationEntries.length > 0, action: 'settings' },
      { id: 'uploads', label: 'Share your first material', done: (profile.materialsCount || 0) > 0, action: 'upload' },
      { id: 'interests', label: 'Add a few interests', done: (profile.hobbies?.length || 0) > 0, action: 'settings' },
      { id: 'links', label: 'Add a link or contact detail', done: (profile.social_links?.length || 0) > 0 || Boolean(profile.website) || Boolean(profile.show_contact_info), action: 'edit' },
    ];
    const done = items.filter((i) => i.done).length;
    return { items, done, percent: Math.round((done / items.length) * 100) };
  }, [profile, educationEntries]);

  const featured = profile?.featuredMaterials || [];
  const featuredIds = profile?.featured_material_ids || [];

  // A visitor should never land on a wall of empty boxes.
  const hasPortfolioContent = Boolean(
    profile && (
      profile.bio || profile.headline || educationEntries.length || teachingEntries.length ||
      profile.recentMaterials?.length || profile.projects?.length ||
      profile.certificates?.length || profile.hobbies?.length || profile.cv
    )
  );

  // Education, projects, certificates and interests are all edited in one
  // place — Settings → Personal information — so the profile stays a view.
  const goToPersonalize = () => navigate(`/${language}/settings?section=personal`);

  const handleSaveEdit = async () => {
    setSaving(true);
    setEditStatus(null);
    try {
      const cleanLinks = editForm.socialLinks.map(l => l.trim()).filter(l => l && isValidUrl(l));
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || ""}/api/auth/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          firstname: editForm.firstname,
          lastname: editForm.lastname,
          username: editForm.username,
          bio: editForm.bio,
          headline: editForm.headline.trim(),
          social_links: cleanLinks,
          show_contact_info: editForm.showContact,
          available_for_tutoring: editForm.openToTutoring,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Update failed.');

      dispatch({ type: 'UPDATE_USER', payload: data.user });
      setProfile(prev => ({
        ...prev,
        firstname: data.user.firstname,
        lastname: data.user.lastname,
        username: data.user.username,
        bio: data.user.bio,
        headline: data.user.headline,
        social_links: data.user.social_links,
        show_contact_info: data.user.show_contact_info,
        available_for_tutoring: data.user.available_for_tutoring,
      }));
      setEditForm(prev => ({
        ...prev,
        socialLinks: data.user.social_links?.length ? data.user.social_links : [],
      }));
      setIsEditing(false);

      if (data.user.username !== username) {
        navigate(`/${data.user.username}`, { replace: true });
      }
    } catch (err) {
      setEditStatus({ type: 'error', message: err.message });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg bg-muted border-0 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring";

  // Only sections with something in them show up for visitors; the owner
  // always sees them all so there's somewhere to add the missing pieces.
  // The server hands the owner their own email/phone regardless; while
  // previewing we re-apply the rule a real visitor would hit, so the preview
  // does not promise contact details nobody else can see.
  const contactHidden = previewAsVisitor && !profile?.show_contact_info;
  const contactEmail = contactHidden ? null : profile?.email;
  const contactNumber = contactHidden ? null : profile?.number;
  const contactAvailable = Boolean(contactEmail || contactNumber || profile?.website || profile?.social_links?.length);

  const sections = useMemo(() => {
    if (!profile) return [];
    const all = [
      { id: 'overview', label: 'Overview', icon: Activity, always: true },
      { id: 'education', label: 'Education', icon: GraduationCap, count: educationEntries.length + teachingEntries.length },
      { id: 'uploads', label: 'Uploads', icon: FileText, count: profile.recentMaterials?.length || 0 },
      { id: 'projects', label: 'Projects', icon: Briefcase, count: profile.projects?.length || 0 },
      { id: 'certificates', label: 'Certificates', icon: Award, count: profile.certificates?.length || 0 },
      { id: 'interests', label: 'Interests', icon: Sparkles, count: profile.hobbies?.length || 0 },
      { id: 'cv', label: 'CV', icon: FileText, count: profile.cv ? 1 : 0 },
      { id: 'connections', label: 'Connections', icon: AtSign, count: contactAvailable ? 1 : 0 },
    ];
    return all.filter((s) => s.always || ownerView || s.count > 0);
  }, [profile, ownerView, educationEntries, teachingEntries, contactAvailable]);

  useEffect(() => {
    if (sections.length && !sections.some((s) => s.id === activeSection)) {
      setActiveSection('overview');
    }
  }, [sections, activeSection]);

  return (
    <div className="min-h-screen bg-background">
      {token ? <Navbar /> : <PublicNavbar />}

      <main className="max-w-5xl mx-auto px-6 py-10">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            <span className="text-sm opacity-40 animate-pulse">Loading profile...</span>
          </div>
        )}

        {!loading && error && (
          <div className="p-8 rounded-2xl bg-card border border-border shadow-xl max-w-md mx-auto text-center">
            <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <h2 className="text-lg font-bold text-foreground mb-2">{error}</h2>
            <button
              onClick={() => navigate(`/${language}/dashboard`)}
              className="mt-4 px-6 py-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all font-medium text-sm"
            >
              Back to Dashboard
            </button>
          </div>
        )}

        {!loading && !error && profile && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="relative"
          >
            {/* Wide screens: floating icon rail pinned to the card's left edge. */}
            <div className="hidden 2xl:block absolute right-full top-0 mr-3 z-20">
              <SectionRail sections={sections} activeSection={activeSection} onSelect={setActiveSection} vertical />
            </div>
            {/* Narrower screens (including regular laptops): the same rail,
                laid flat above the card. The floating version only appears
                once the viewport is wide enough that it can't clip past
                the edge of the screen. */}
            <div className="2xl:hidden mb-4">
              <SectionRail sections={sections} activeSection={activeSection} onSelect={setActiveSection} vertical={false} />
            </div>

            <div className="bg-card border border-border rounded-2xl shadow-xl overflow-hidden">
            <div
              className="relative h-56 bg-gradient-to-br from-primary/30 to-secondary/30 bg-cover bg-center group/banner"
              style={profile.banner_url ? { backgroundImage: `url(${profile.banner_url})` } : undefined}
            >
              {ownerView && (
                <button
                  type="button"
                  onClick={() => openImageUrlModal('banner')}
                  className="absolute inset-0 w-full flex items-center justify-center bg-black/0 group-hover/banner:bg-black/40 opacity-0 group-hover/banner:opacity-100 transition-all cursor-pointer border-none"
                >
                  <span className="flex items-center gap-1.5 text-white text-xs font-bold">
                    <Pencil className="w-3.5 h-3.5" /> Edit banner
                  </span>
                </button>
              )}
            </div>

            {/* ── Identity header ── */}
            <div className="px-8 pb-6 pt-0 border-b border-border bg-muted/30">
              <div className="flex items-start gap-5">
                <div className="relative -mt-12 w-24 h-24 rounded-2xl flex-shrink-0 group/avatar">
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt={profile.username} className="w-24 h-24 rounded-2xl object-cover border-4 border-card" />
                  ) : (
                    <div className="w-24 h-24 rounded-2xl bg-secondary text-secondary-foreground flex items-center justify-center font-black text-2xl border-4 border-card">
                      {initials}
                    </div>
                  )}
                  {ownerView && (
                    <button
                      type="button"
                      onClick={() => openImageUrlModal('avatar')}
                      className="absolute inset-0 w-full rounded-2xl flex items-center justify-center bg-black/0 group-hover/avatar:bg-black/40 opacity-0 group-hover/avatar:opacity-100 transition-all cursor-pointer border-none"
                    >
                      <Pencil className="w-4 h-4 text-white" />
                    </button>
                  )}
                </div>

                <div className="min-w-0 flex-1 pt-3">
                  <h1 className="text-2xl font-bold text-foreground truncate">
                    {profile.firstname} {profile.lastname}
                  </h1>
                  {profile.headline ? (
                    <p className="text-sm text-foreground/90 font-medium mt-1 max-w-2xl">{profile.headline}</p>
                  ) : ownerView ? (
                    <button
                      onClick={() => { setIsEditing(true); setEditStatus(null); }}
                      className="mt-1 text-sm text-primary font-medium bg-transparent border-none p-0 cursor-pointer hover:underline"
                    >
                      + Add a headline — say what you study and share
                    </button>
                  ) : null}
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1.5">
                    <span className="text-xs text-muted-foreground font-medium">@{profile.username}</span>
                    {profile.location && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" /> {profile.location}
                      </span>
                    )}
                    {memberSince && (
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarDays className="w-3 h-3" /> Joined {memberSince}
                      </span>
                    )}
                    {profile.available_for_tutoring && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 text-[11px] font-bold">
                        <Handshake className="w-3 h-3" /> Open to tutoring
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 pt-3">
                  {profile.social_links?.length > 0 && profile.social_links.map((link, idx) => {
                    const { icon: Icon, label, url } = detectPlatform(link);
                    return (
                      <button
                        key={idx}
                        onClick={() => openExternalLink(url)}
                        title={label}
                        className="w-9 h-9 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center transition-colors border-none cursor-pointer"
                      >
                        <Icon className="w-4 h-4 text-foreground" />
                      </button>
                    );
                  })}
                  {!isSelf && token && (
                    <button
                      onClick={handleToggleFollow}
                      disabled={followBusy}
                      className={`flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold transition-colors border-none cursor-pointer disabled:opacity-60 ${
                        profile.isFollowing ? "bg-muted text-foreground hover:bg-destructive/10 hover:text-destructive" : "bg-primary text-primary-foreground hover:bg-primary/90"
                      }`}
                    >
                      {profile.isFollowing ? <><UserCheck className="w-4 h-4" /> Following</> : <><UserPlus className="w-4 h-4" /> Follow</>}
                    </button>
                  )}
                  {!isSelf && !token && (
                    <button
                      onClick={() => navigate(`/${language}/login`)}
                      className="flex items-center gap-2 px-4 h-10 rounded-xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors border-none cursor-pointer"
                    >
                      <UserPlus className="w-4 h-4" /> Follow
                    </button>
                  )}
                  <button
                    onClick={handleShare}
                    title="Copy link to this profile"
                    className={`flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-bold transition-colors border-none cursor-pointer ${
                      linkCopied ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted text-foreground hover:bg-muted/70'
                    }`}
                  >
                    {linkCopied ? <><Check className="w-4 h-4" /> Copied</> : <><Share2 className="w-4 h-4" /> Share</>}
                  </button>
                  {isSelf && (
                    <button
                      onClick={() => setPreviewAsVisitor((prev) => !prev)}
                      title={previewAsVisitor ? 'Back to your own view' : 'See your profile the way visitors see it'}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors border-none cursor-pointer ${
                        previewAsVisitor ? 'bg-primary/10 text-primary' : 'bg-muted hover:bg-muted/70 text-foreground'
                      }`}
                    >
                      {previewAsVisitor ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                  {ownerView && (
                    <button
                      onClick={() => { setIsEditing(prev => !prev); setEditStatus(null); }}
                      className="w-10 h-10 rounded-xl bg-muted hover:bg-muted/70 flex items-center justify-center transition-colors border-none cursor-pointer"
                      title="Edit profile"
                    >
                      {isEditing ? <X className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mt-5 pt-4 border-t border-border/60">
                <span className="text-xs text-foreground">
                  <b className="font-bold">{formatCount(profile.followerCount || 0)}</b> <span className="text-muted-foreground">Followers</span>
                </span>
                <span className="text-xs text-foreground">
                  <b className="font-bold">{formatCount(profile.materialsCount || 0)}</b> <span className="text-muted-foreground">Materials</span>
                </span>
                {profile.cv && (
                  <a
                    href={profile.cv.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                  >
                    <FileText className="w-3.5 h-3.5" /> View CV
                  </a>
                )}
                {previewAsVisitor && (
                  <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-bold">
                    <Eye className="w-3 h-3" /> Visitor preview
                  </span>
                )}
              </div>
            </div>

            {/* ── Edit panel ── */}
            <AnimatePresence initial={false}>
              {ownerView && isEditing && (
                <motion.div
                  key="edit"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  style={{ overflow: 'hidden' }}
                  className="border-b border-border bg-muted/10"
                >
                  <div className="p-8 space-y-4">
                    <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Edit Profile</h2>

                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="text-xs font-bold text-foreground mb-1.5 block">First name</label>
                        <input className={inputClass} value={editForm.firstname} onChange={(e) => setEditForm(prev => ({ ...prev, firstname: e.target.value }))} />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-bold text-foreground mb-1.5 block">Last name</label>
                        <input className={inputClass} value={editForm.lastname} onChange={(e) => setEditForm(prev => ({ ...prev, lastname: e.target.value }))} />
                      </div>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-foreground mb-1.5 block">Username</label>
                      <input
                        className={inputClass}
                        value={editForm.username}
                        onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value.trim().toLowerCase() }))}
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">At least 6 characters — this also changes your profile URL.</p>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-foreground mb-1.5 block">Headline</label>
                      <input
                        className={inputClass}
                        value={editForm.headline}
                        maxLength={140}
                        onChange={(e) => setEditForm(prev => ({ ...prev, headline: e.target.value }))}
                        placeholder="e.g. Chemistry student sharing organic chemistry notes"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">
                        One line under your name. {140 - editForm.headline.length} characters left.
                      </p>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-foreground mb-1.5 block">Bio</label>
                      <textarea
                        className={`${inputClass} resize-none`}
                        rows={3}
                        value={editForm.bio}
                        onChange={(e) => handleBioChange(e.target.value)}
                        placeholder="Tell others a bit about yourself..."
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">{countWords(editForm.bio)}/{MAX_BIO_WORDS} words</p>
                    </div>

                    {/* Links */}
                    <div>
                      <label className="text-xs font-bold text-foreground mb-1.5 block">Links</label>
                      <div className="space-y-2">
                        {editForm.socialLinks.map((link, idx) => {
                          const preview = link.trim() ? detectPlatform(link) : null;
                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                {preview ? <preview.icon className="w-4 h-4 text-muted-foreground" /> : <Plus className="w-4 h-4 text-muted-foreground opacity-30" />}
                              </div>
                              <input className={inputClass} value={link} onChange={(e) => handleLinkChange(idx, e.target.value)} placeholder="https://github.com/yourusername" />
                              <button onClick={() => removeLinkField(idx)} className="w-8 h-8 rounded-lg bg-muted hover:bg-destructive/10 hover:text-destructive flex items-center justify-center flex-shrink-0 border-none cursor-pointer transition-colors" title="Remove link">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button onClick={addLinkField} className="mt-2 flex items-center gap-1.5 text-xs font-bold text-primary bg-transparent border-none cursor-pointer hover:underline">
                        <Plus className="w-3.5 h-3.5" /> Add link
                      </button>
                    </div>

                    {/* Availability */}
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-muted/50">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-foreground">Open to tutoring</p>
                        <p className="text-[11px] text-muted-foreground">Shows a badge on your profile so people know they can ask you for help.</p>
                      </div>
                      <button
                        onClick={() => setEditForm(prev => ({ ...prev, openToTutoring: !prev.openToTutoring }))}
                        role="switch"
                        aria-checked={editForm.openToTutoring}
                        className={`relative w-11 h-6 rounded-full flex-shrink-0 border-none cursor-pointer transition-colors ${editForm.openToTutoring ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editForm.openToTutoring ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>

                    {/* Contact sharing */}
                    <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-muted/50">
                      <div className="min-w-0">
                        <p className="text-xs font-bold text-foreground">Show my contact details</p>
                        <p className="text-[11px] text-muted-foreground">Signed-in visitors can reveal your email and phone from the Connections section.</p>
                      </div>
                      <button
                        onClick={() => setEditForm(prev => ({ ...prev, showContact: !prev.showContact }))}
                        role="switch"
                        aria-checked={editForm.showContact}
                        className={`relative w-11 h-6 rounded-full flex-shrink-0 border-none cursor-pointer transition-colors ${editForm.showContact ? 'bg-primary' : 'bg-muted-foreground/30'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editForm.showContact ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>

                    <button
                      onClick={goToPersonalize}
                      className="w-full flex items-center justify-between gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted text-left transition-colors border-none cursor-pointer"
                    >
                      <span className="min-w-0">
                        <span className="block text-xs font-bold text-foreground">Education, projects, certificates & interests</span>
                        <span className="block text-[11px] text-muted-foreground">Managed in Settings → Personal information.</span>
                      </span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    </button>

                    <button
                      onClick={handleSaveEdit}
                      disabled={saving || !editForm.firstname || !editForm.lastname || !editForm.username}
                      className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2 border-none cursor-pointer"
                    >
                      {saving ? 'Saving...' : <><Check className="w-4 h-4" /> Save Changes</>}
                    </button>
                    <StatusLine status={editStatus} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Sections ── */}
            <div>
              <div className="p-8 min-h-[320px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeSection}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                  >
                    {activeSection === 'overview' && (
                      <div className="space-y-8">
                        {/* Nothing here yet, and nobody to fix it - say so once,
                            calmly, instead of showing seven empty boxes. */}
                        {!hasPortfolioContent && !ownerView && (
                          <div className="py-12 text-center">
                            <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                              <Sparkles className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <p className="text-sm font-bold text-foreground">This profile is just getting started</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {profile.firstname} has not shared anything publicly yet.
                            </p>
                          </div>
                        )}

                        {/* Setup checklist - owner only, and only until it is done. */}
                        {ownerView && completeness.percent < 100 && (
                          <div className="p-5 rounded-2xl border border-primary/20 bg-primary/5">
                            <div className="flex items-center justify-between gap-3 mb-1">
                              <h3 className="text-sm font-bold text-foreground">Finish your profile</h3>
                              <span className="text-xs font-bold text-primary">{completeness.percent}%</span>
                            </div>
                            <p className="text-[11px] text-muted-foreground mb-3">
                              A complete profile is what makes this link worth sending to someone.
                            </p>
                            <div className="h-1.5 rounded-full bg-primary/15 overflow-hidden mb-4">
                              <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${completeness.percent}%` }} />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {completeness.items.filter((item) => !item.done).map((item) => (
                                <button
                                  key={item.id}
                                  onClick={() => {
                                    if (item.action === 'settings') goToPersonalize();
                                    else if (item.action === 'avatar') openImageUrlModal('avatar');
                                    else if (item.action === 'upload') navigate(`/${language}/library`);
                                    else { setIsEditing(true); setEditStatus(null); }
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card border border-border text-[11px] font-bold text-foreground hover:border-primary hover:text-primary transition-colors cursor-pointer"
                                >
                                  <Plus className="w-3 h-3" /> {item.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* About */}
                        {(profile.bio || ownerView) && (
                          <div>
                            <SectionHeading
                              action={ownerView && <ManageButton onClick={() => { setIsEditing(true); setEditStatus(null); }} label={profile.bio ? 'Edit' : 'Add'} icon={profile.bio ? Pencil : Plus} />}
                            >
                              About
                            </SectionHeading>
                            {profile.bio ? (
                              <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line max-w-3xl">{profile.bio}</p>
                            ) : (
                              <EmptySection>Tell people what you study, what you share here, and what you are looking for.</EmptySection>
                            )}
                          </div>
                        )}

                        {/* Stats */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {[
                            { label: 'Materials', value: formatCount(profile.materialsCount || 0), icon: FileText },
                            { label: 'Points', value: formatCount(profile.points || 0), icon: Sparkles },
                            { label: 'Learning time', value: `${Math.round((profile.total_learning_time || 0) / 60)}h`, icon: Activity },
                            { label: 'Followers', value: formatCount(profile.followerCount || 0), icon: TrendingUp },
                          ].map((stat) => (
                            <div key={stat.label} className="p-4 rounded-xl bg-muted/50">
                              <stat.icon className="w-4 h-4 text-primary mb-2" />
                              <p className="text-2xl font-black text-foreground leading-none">{stat.value}</p>
                              <p className="text-[11px] text-muted-foreground font-medium mt-1.5">{stat.label}</p>
                            </div>
                          ))}
                        </div>

                        {/* Featured work */}
                        {(featured.length > 0 || (ownerView && profile.recentMaterials?.length > 0)) && (
                          <div>
                            <SectionHeading
                              action={ownerView && profile.recentMaterials?.length > 0 && (
                                <ManageButton onClick={() => setActiveSection('uploads')} label="Choose" icon={Star} />
                              )}
                            >
                              Featured work
                            </SectionHeading>
                            {featured.length === 0 ? (
                              <EmptySection
                                action={<ManageButton onClick={() => setActiveSection('uploads')} label="Pick from your uploads" icon={Star} />}
                              >
                                Pin up to three materials you want people to see first.
                              </EmptySection>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                {featured.map((m) => {
                                  const Icon = CATEGORY_ICON[m.category] || FileText;
                                  return (
                                    <button
                                      key={m.id}
                                      onClick={() => navigate(`/${language}/material/${m.id}`)}
                                      className="group text-left rounded-xl overflow-hidden bg-muted/50 hover:bg-muted transition-colors border-none cursor-pointer p-0"
                                    >
                                      <div className="relative aspect-[16/9] bg-primary/10 text-primary flex items-center justify-center overflow-hidden">
                                        {m.cover_url ? (
                                          <img src={m.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                                        ) : (
                                          <Icon className="w-6 h-6" />
                                        )}
                                      </div>
                                      <div className="p-3">
                                        <p className="text-xs font-bold text-foreground line-clamp-2">{m.title}</p>
                                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-medium mt-1.5">
                                          <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatCount(m.views)}</span>
                                          {m.rating > 0 && <span>* {Number(m.rating).toFixed(1)}</span>}
                                        </div>
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Education / credentials at a glance */}
                        {(primaryEducation || profile.certificates?.length > 0 || profile.projects?.length > 0) && (
                          <div className="grid sm:grid-cols-2 gap-3">
                            {primaryEducation && (
                              <button
                                onClick={() => setActiveSection('education')}
                                className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted text-left transition-colors border-none cursor-pointer"
                              >
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                  <GraduationCap className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-bold text-foreground truncate">{primaryEducation.institution || 'Education'}</p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {[primaryEducation.level, primaryEducation.major].filter(Boolean).join(' - ') || '-'}
                                  </p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                              </button>
                            )}
                            {profile.certificates?.length > 0 && (
                              <button
                                onClick={() => setActiveSection('certificates')}
                                className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted text-left transition-colors border-none cursor-pointer"
                              >
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                  <BadgeCheck className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-bold text-foreground truncate">
                                    {profile.certificates.length} certificate{profile.certificates.length === 1 ? '' : 's'}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">{profile.certificates[0]?.name}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                              </button>
                            )}
                            {profile.projects?.length > 0 && (
                              <button
                                onClick={() => setActiveSection('projects')}
                                className="flex items-center gap-3 p-4 rounded-xl bg-muted/50 hover:bg-muted text-left transition-colors border-none cursor-pointer"
                              >
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                  <Briefcase className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-bold text-foreground truncate">
                                    {profile.projects.length} project{profile.projects.length === 1 ? '' : 's'}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">{profile.projects[0]?.title}</p>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                              </button>
                            )}
                          </div>
                        )}

                        {/* Interests */}
                        {profile.hobbies?.length > 0 && (
                          <div>
                            <SectionHeading action={ownerView && <ManageButton onClick={goToPersonalize} />}>Interests</SectionHeading>
                            <div className="flex flex-wrap gap-1.5">
                              {profile.hobbies.slice(0, 12).map((interest, idx) => (
                                <span key={idx} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                  {interest}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Activity */}
                        <ContributionHeatmap username={profile.username} token={token} isSelf={isSelf} />

                        {/* Latest uploads */}
                        {profile.recentMaterials?.length > 0 && (
                          <div>
                            <SectionHeading
                              action={(
                                <button
                                  onClick={() => setActiveSection('uploads')}
                                  className="text-[11px] font-bold text-primary bg-transparent border-none cursor-pointer hover:underline"
                                >
                                  See all
                                </button>
                              )}
                            >
                              Latest uploads
                            </SectionHeading>
                            <div className="grid sm:grid-cols-2 gap-2">
                              {profile.recentMaterials.slice(0, 4).map((m) => {
                                const Icon = CATEGORY_ICON[m.category] || FileText;
                                return (
                                  <button
                                    key={m.id}
                                    onClick={() => navigate(`/${language}/material/${m.id}`)}
                                    className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted text-left transition-colors border-none cursor-pointer"
                                  >
                                    <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                      {m.cover_url ? (
                                        <img src={m.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                      ) : (
                                        <Icon className="w-4 h-4" />
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-bold text-foreground truncate">{m.title}</p>
                                      <span className="flex items-center gap-1 text-[10px] text-muted-foreground font-medium mt-0.5">
                                        <Eye className="w-3 h-3" /> {formatCount(m.views)}
                                      </span>
                                    </div>
                                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Get in touch */}
                        {contactAvailable && !ownerView && (
                          <div className="flex items-center justify-between gap-4 p-5 rounded-2xl bg-muted/50">
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-foreground">Want to reach {profile.firstname}?</p>
                              <p className="text-[11px] text-muted-foreground mt-0.5">
                                {profile.available_for_tutoring ? 'Open to tutoring and questions.' : 'Contact details are in the Connections section.'}
                              </p>
                            </div>
                            <button
                              onClick={() => setActiveSection('connections')}
                              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 border-none cursor-pointer flex-shrink-0"
                            >
                              <AtSign className="w-3.5 h-3.5" /> Get in touch
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'education' && (
                      <div>
                        <SectionHeading action={ownerView && <ManageButton onClick={goToPersonalize} />}>Education</SectionHeading>
                        {educationEntries.length === 0 && teachingEntries.length === 0 ? (
                          <EmptySection action={ownerView && <ManageButton onClick={goToPersonalize} label="Add education" icon={Plus} />}>
                            {ownerView ? 'Add where you study or teach so people know your background.' : 'No education details added yet.'}
                          </EmptySection>
                        ) : (
                          <div className="space-y-3">
                            {educationEntries.map((entry, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                  <GraduationCap className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-foreground">{entry.institution || 'Institution'}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {[entry.level, entry.major].filter(Boolean).join(' · ') || '—'}
                                  </p>
                                  {entry.year && (
                                    <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                                      <CalendarDays className="w-3 h-3" /> {entry.year}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                            {teachingEntries.map((entry, idx) => (
                              <div key={`t-${idx}`} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                  <Building2 className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-sm font-bold text-foreground">{entry.workplace || entry.institution || 'Teaching experience'}</p>
                                  <p className="text-xs text-muted-foreground">{entry.subject || entry.major || '—'}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'uploads' && (
                      <div>
                        <SectionHeading>Public uploads</SectionHeading>
                        {ownerView && profile.recentMaterials?.length > 0 && (
                          <p className="text-[11px] text-muted-foreground -mt-2 mb-3">
                            Tap the star to feature a material at the top of your profile (up to 3).
                          </p>
                        )}
                        {!profile.recentMaterials?.length ? (
                          <EmptySection
                            action={ownerView && <ManageButton onClick={() => navigate(`/${language}/library`)} label="Upload a material" icon={Upload} />}
                          >
                            {ownerView ? 'Your public materials show up here — this is the part visitors actually browse.' : 'Nothing shared publicly yet.'}
                          </EmptySection>
                        ) : (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {profile.recentMaterials.map((m) => {
                              const Icon = CATEGORY_ICON[m.category] || FileText;
                              const isPinned = featuredIds.includes(m.id);
                              return (
                                <div
                                  key={m.id}
                                  className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                                >
                                  <button
                                    onClick={() => navigate(`/${language}/material/${m.id}`)}
                                    className="flex items-center gap-3 min-w-0 flex-1 bg-transparent border-none cursor-pointer text-left p-0"
                                  >
                                    <div className="relative w-10 h-10 rounded-lg overflow-hidden bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                      {m.cover_url ? (
                                        <img src={m.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                                      ) : (
                                        <Icon className="w-4 h-4" />
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-xs font-bold text-foreground truncate">{m.title}</p>
                                      <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-medium mt-0.5">
                                        <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {formatCount(m.views)}</span>
                                        {m.rating > 0 && <span>★ {Number(m.rating).toFixed(1)}</span>}
                                      </div>
                                    </div>
                                  </button>
                                  {ownerView ? (
                                    <button
                                      onClick={() => handleToggleFeatured(m.id)}
                                      disabled={pinBusy}
                                      title={isPinned ? 'Remove from featured' : 'Feature on your profile'}
                                      className={`w-8 h-8 rounded-lg flex items-center justify-center border-none cursor-pointer transition-colors flex-shrink-0 disabled:opacity-50 ${
                                        isPinned ? 'bg-amber-500/15 text-amber-600' : 'bg-background text-muted-foreground hover:text-amber-600'
                                      }`}
                                    >
                                      <Star className={`w-3.5 h-3.5 ${isPinned ? 'fill-current' : ''}`} />
                                    </button>
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 flex-shrink-0" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'projects' && (
                      <div>
                        <SectionHeading action={ownerView && <ManageButton onClick={goToPersonalize} />}>Projects</SectionHeading>
                        {!profile.projects?.length ? (
                          <EmptySection action={ownerView && <ManageButton onClick={goToPersonalize} label="Add project" icon={Plus} />}>
                            {ownerView ? 'Show what you have built or worked on.' : 'No projects listed yet.'}
                          </EmptySection>
                        ) : (
                          <div className="space-y-3">
                            {profile.projects.map((project, idx) => (
                              <div key={idx} className="p-4 rounded-xl bg-muted/50">
                                <div className="flex items-start justify-between gap-3">
                                  <p className="text-sm font-bold text-foreground">{project.title}</p>
                                  {project.year && <span className="text-[11px] text-muted-foreground flex-shrink-0">{project.year}</span>}
                                </div>
                                {project.description && <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{project.description}</p>}
                                {project.link && (
                                  <button
                                    onClick={() => openExternalLink(project.link)}
                                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-primary bg-transparent border-none cursor-pointer hover:underline"
                                  >
                                    <ExternalLink className="w-3 h-3" /> Open project
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'certificates' && (
                      <div>
                        <SectionHeading action={ownerView && <ManageButton onClick={goToPersonalize} />}>Certificates</SectionHeading>
                        {!profile.certificates?.length ? (
                          <EmptySection action={ownerView && <ManageButton onClick={goToPersonalize} label="Add certificate" icon={Plus} />}>
                            {ownerView ? 'Add the courses and credentials you have earned.' : 'No certificates listed yet.'}
                          </EmptySection>
                        ) : (
                          <div className="space-y-3">
                            {profile.certificates.map((cert, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50">
                                <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                                  <Award className="w-4 h-4" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-bold text-foreground">{cert.name}</p>
                                  <p className="text-xs text-muted-foreground">{[cert.issuer, cert.year].filter(Boolean).join(' · ') || '—'}</p>
                                  {cert.url && (
                                    <button
                                      onClick={() => openExternalLink(cert.url)}
                                      className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold text-primary bg-transparent border-none cursor-pointer hover:underline"
                                    >
                                      <ExternalLink className="w-3 h-3" /> View credential
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'interests' && (
                      <div>
                        <SectionHeading action={ownerView && <ManageButton onClick={goToPersonalize} />}>Interests</SectionHeading>
                        {!profile.hobbies?.length ? (
                          <EmptySection action={ownerView && <ManageButton onClick={goToPersonalize} label="Add interests" icon={Plus} />}>
                            {ownerView ? 'Interests help us recommend materials you actually care about.' : 'No interests added yet.'}
                          </EmptySection>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {profile.hobbies.map((interest, idx) => (
                              <span key={idx} className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-medium">
                                {interest}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'cv' && (
                      <div>
                        <SectionHeading>CV</SectionHeading>
                        {!profile.cv && !ownerView ? (
                          <EmptySection>No CV shared.</EmptySection>
                        ) : (
                          <div className="flex items-center gap-3">
                            {profile.cv && (
                              <a
                                href={profile.cv.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted hover:bg-muted/70 text-sm font-medium text-foreground transition-colors"
                              >
                                <FileText className="w-4 h-4" /> View CV
                              </a>
                            )}
                            {ownerView && (
                              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted hover:bg-muted/70 text-sm font-medium text-foreground transition-colors cursor-pointer">
                                <Upload className="w-4 h-4" />
                                {uploadingCv ? 'Uploading...' : profile.cv ? 'Replace CV' : 'Upload CV'}
                                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleCvUpload} disabled={uploadingCv} />
                              </label>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {activeSection === 'connections' && (
                      <div>
                        <SectionHeading>Connections</SectionHeading>
                        {!contactAvailable ? (
                          <EmptySection>
                            {ownerView
                              ? 'Turn on "Show my contact details" in Edit profile so others can reach you.'
                              : "This person hasn't shared any contact details."}
                          </EmptySection>
                        ) : !contactRevealed ? (
                          <div className="p-6 rounded-xl bg-muted/50 text-center">
                            <AtSign className="w-6 h-6 text-primary mx-auto mb-3" />
                            <p className="text-xs text-muted-foreground mb-4">
                              {ownerView ? 'This is what visitors see when they ask to get in touch.' : `Get in touch with ${profile.firstname}.`}
                            </p>
                            <button
                              onClick={() => setContactRevealed(true)}
                              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 border-none cursor-pointer"
                            >
                              Show contact details
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {contactEmail && (
                              <a href={`mailto:${contactEmail}`} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                                <AtSign className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                <span className="text-xs font-medium text-foreground truncate">{contactEmail}</span>
                              </a>
                            )}
                            {contactNumber && (
                              <a href={`tel:${contactNumber}`} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                                <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                <span className="text-xs font-medium text-foreground">{contactNumber}</span>
                              </a>
                            )}
                            {profile.website && (
                              <button onClick={() => openExternalLink(profile.website)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors border-none cursor-pointer text-left">
                                <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                <span className="text-xs font-medium text-foreground truncate">{profile.website}</span>
                              </button>
                            )}
                            {profile.social_links?.map((link, idx) => {
                              const { icon: Icon, label, url } = detectPlatform(link);
                              return (
                                <button key={idx} onClick={() => openExternalLink(url)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-muted/50 hover:bg-muted transition-colors border-none cursor-pointer text-left">
                                  <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                  <span className="text-xs font-medium text-foreground truncate">{label}</span>
                                </button>
                              );
                            })}
                            {!contactEmail && !contactNumber && (
                              <p className="text-[11px] text-muted-foreground">
                                Email and phone stay private unless this person turns sharing on.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
            </div>
          </motion.div>
        )}
      </main>

      <ImageUrlModal
        isOpen={imageUrlModal.open}
        type={imageUrlModal.type}
        error={imageUrlError}
        saving={uploadingImage}
        onCancel={() => setImageUrlModal({ open: false, type: null })}
        onSave={handleSaveImageUrl}
      />
    </div>
  );
};

export default Profile;
